import React, { useRef, useState, useEffect } from 'react';
import classes from './ReadMore.module.scss';
import { IconsEnum } from '../../../constants/icons';
import { Icon } from '../../images/Icon/Icon';
import classNames from 'classnames';
import { Tooltip } from '../../actions/Tooltip';
import { useDimensions } from '../../../hooks/useDimensions';
import { useDidMount } from '../../../hooks/useDidMount';

export type ReadMoreViewProps = {
  readMoreKey: string;
  icon: IconsEnum;
  text: string;
};

export const ReadMoreView = ({ icon, text, readMoreKey }: ReadMoreViewProps): JSX.Element => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowed, setIsOverflowed] = useState<boolean>(false);
  const { width } = useDimensions(contentRef, 0);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);

  // This handles the determination when the element changes width
  useEffect(() => {
    const checkOverflow = () => {
      const { current: contentElement } = contentRef;
      // @TODO: we need to automate this value
      // i.e. we need to calculate the siblings
      // We might want to consider passing a ref for this? or supply the value itself?
      const referenceWidth = 510;

      if (contentElement) {
        const element = contentElement;
        const currentWidth = element.offsetWidth;

        // If our current width is very close to the maximum width don't show "read more"
        if (maxWidth && currentWidth >= maxWidth) {
          setIsOverflowed(false);
          return;
        }

        // Check if the width has changed before proceeding
        if (width !== currentWidth || currentWidth > referenceWidth) {
          const overflowed = currentWidth + 2 < element.scrollWidth;
          const nonOverflowed = currentWidth - 2 > element.scrollWidth;

          if (overflowed || nonOverflowed) {
            setIsOverflowed(overflowed);
          }
        }
      }
    };

    checkOverflow();
  }, [text, width, maxWidth]);

  // This will get the max size of the element
  useDidMount(() => {
    const { current: contentElement } = contentRef;

    if (contentElement) {
      // Backup original styles
      const originalWidth = contentElement.style.width;

      // Set element's width to a large value and measure its `scrollWidth`
      contentElement.style.width = '10000px';
      const maxElementWidth = contentElement.scrollWidth;

      // Restore original width
      contentElement.style.width = originalWidth;

      setMaxWidth(maxElementWidth);
    }
  });

  const contentClasses = classNames('grid-x', classes.Content);
  const textClasses = classNames('cell shrink', classes.Text, {
    [classes.WithEllipsis]: isOverflowed
  });
  const readMoreLinkClasses = classNames('cell grow', classes.ReadMoreLink, {
    [classes.ReadMoreLinkHidden]: !isOverflowed
  });

  return (
    <div className={classes.ReadMore} ref={contentRef}>
      <Icon name={icon} size={'large'} />
      <div className={contentClasses}>
        <div className={textClasses} style={isOverflowed && width ? { width: `${width - 100}px` } : {}}>
          {text}
        </div>
        {isOverflowed && (
          <>
            <Tooltip type={'regular'} pointer={true} position={'bottom'} target={`#${readMoreKey}`}>
              <>{text}</>
            </Tooltip>
            <div className={readMoreLinkClasses} id={readMoreKey}>
              Read more
            </div>
          </>
        )}
      </div>
    </div>
  );
};
